import { Chat, Message, User } from '@graphql/generated';
import { Search } from '@root/types';

// Chat Search shows the first CHAT_RESULTS_PREVIEW_COUNT chat results, then
// "View all" (TA-11072).
export const CHAT_RESULTS_PREVIEW_COUNT = 5;

// search.graphql aliases a Chat result's `users` to `chatUsers` (Group's
// nullable `users` in the same union would otherwise conflict), so map it back
// before handing the record to ChatCard, which reads `chat.users`.
export const toChatFromSearchResult = (item: Search<Chat>): Chat => {
  const { chatUsers, ...chat } = item.record as Chat & { chatUsers?: User[] };
  return { ...chat, users: chatUsers ?? chat.users ?? [] };
};

// search.graphql selects a Message's author as `author { name }`, not
// `authorName`, so reading `authorName` alone rendered "undefined:" in mobile
// Chat history results (TA-11111). `authorName` stays as a fallback for records
// that do carry it.
export const searchMessageAuthorName = (
  record: Pick<Message, 'author' | 'authorName'>
): string => record.author?.name || record.authorName || '';

// Appends a page of chat results, skipping chats already listed — each type
// paginates independently on the backend, but a re-sent page must not double up.
export const appendChatResults = (
  existing: Search<Chat>[],
  incoming: Search<Chat>[]
): Search<Chat>[] => {
  const seen = new Set(existing.map((c) => c.record.id));
  return [...existing, ...incoming.filter((c) => !seen.has(c.record.id))];
};

// Mobile only: a 1:1 chat with someone already listed under Contacts is the
// same destination twice, so drop it. Group chats always show (TA-11072).
export const excludeChatsWithListedContacts = (
  chats: Search<Chat>[],
  contacts: Search<User>[]
): Search<Chat>[] => {
  if (contacts.length === 0) return chats;
  const contactIds = new Set(contacts.map((c) => c.record.id));
  return chats.filter((item) => {
    if (item.record.isGroupChat) return true;
    return !toChatFromSearchResult(item).users.some((u) =>
      contactIds.has(u.id)
    );
  });
};

// Backend total for the Chats header, less the chats the Contacts dedupe hid.
// Only loaded pages can be deduped, so this is exact once every page is in;
// never below what is actually listed (TA-11072).
export const visibleChatsTotal = (
  chats: Search<Chat>[],
  visibleChats: Search<Chat>[]
): number => {
  const hiddenCount = chats.length - visibleChats.length;
  return Math.max(visibleChats.length, (chats[0]?.total ?? 0) - hiddenCount);
};

// Drops sections with no rows so a match in one section doesn't render "(0)"
// headers for the others. Keeps every section when all are empty — one of
// them anchors the no-results / type-more state. The load-more footer lives on
// the last section (Chat history), so it moves to the last section kept.
export const dropEmptySections = <
  T extends { data: readonly unknown[]; sectionFooter?: unknown },
>(
  lists: T[]
): T[] => {
  const kept = lists.filter((list) => list.data.length > 0);
  if (kept.length === 0) return lists;
  const footer = lists[lists.length - 1].sectionFooter;
  const last = kept[kept.length - 1];
  if (!footer || last.sectionFooter) return kept;
  return [...kept.slice(0, -1), { ...last, sectionFooter: footer }];
};

// Loaded count of a result type that still has more pages on the backend, or
// 0 once it is fully loaded — the next `from` when paging that type.
export const pendingPageOffset = (results: Array<{ total: number }>): number =>
  results.length > 0 && results.length < results[0].total ? results.length : 0;
